import type { Contact } from '@/lib/contacts/types'
import type { Owner } from '@/components/OwnedChip'
import type { PagedResponse } from '@/components/templates/list-pager'

export type InvoiceStatus = 'Unpaid' | 'Partially Paid' | 'Paid' | 'Voided'
export type InvoiceStatusFilter = 'all' | InvoiceStatus

export interface InvoiceLineItem {
  id: number
  description: string
  amount: string
  amount_cents: number
  tax_rate?: { id: number; name: string; percentage: string }
  tax?: string
  tax_cents?: number
  purchaser?: InvoicePurchaser | null
  course?: InvoiceLink | null
}

export interface InvoiceLink {
  id: number
  name: string
}

export interface InvoicePurchaser {
  id: number | null
  display_name: string
  membership_level_name: string | null
}

export interface InvoiceTaxRow {
  name: string
  percentage: string
  amount: string
  amount_cents: number
}

export interface InvoiceBreakdownRow {
  name: string
  kind: string
  percentage: string
  net: string
  net_cents: number
  tax: string
  tax_cents: number
  label: string
}

export interface InvoiceDiscountRow {
  code: string
  label: string
  percent: number | null
  fixed_amount?: string | null
  fixed_amount_cents?: number | null
  reduction: string
  reduction_cents: number
}

export interface InvoiceHistoryEntry {
  id: number
  event: string
  actor: string | null
  amount?: string | null
  amount_cents?: number | null
  balance?: string | null
  balance_cents?: number | null
  payment_method?: string | null
  paid_on?: string | null
  reference?: string | null
  invoice_number?: number | null
  admin_action_id?: string | null
  occurred_at: string
}

export type InvoiceOrigin =
  | 'Manual'
  | 'Sponsorship'
  | 'Training'
  | 'Renewal'
  | 'Application'
  | 'Level change'
  | 'Event'
  | 'Donation'
export type ManualInvoiceOrigin = 'manual' | 'sponsorship' | 'training'
// Every Invoice origin from ADR 0066, in the order staff choose among them.
export const INVOICE_ORIGINS: readonly InvoiceOrigin[] = [
  'Manual',
  'Sponsorship',
  'Training',
  'Renewal',
  'Application',
  'Level change',
  'Event',
  'Donation',
]
export type InvoiceOriginFilter = 'all' | InvoiceOrigin

export interface Invoice {
  id: number
  number: number
  contact_id: number
  contact_name: string
  contact: Contact
  origin: InvoiceOrigin
  event?: InvoiceLink | null
  category_label?: string
  personal_purchase?: boolean
  status: InvoiceStatus
  issue_on: string
  due_on: string
  currency: string
  total: string
  total_cents: number
  subtotal?: string
  subtotal_cents?: number
  tax?: string
  tax_cents?: number
  tax_rows?: InvoiceTaxRow[]
  breakdown?: InvoiceBreakdownRow[]
  total_tax?: string
  total_tax_cents?: number
  tax_total_label?: string
  legend?: string | null
  gross?: string
  gross_cents?: number
  discount?: string
  discount_cents?: number
  discount_row?: InvoiceDiscountRow
  discount_code_change_available?: boolean
  discount_code_change_unavailable_reason?: string | null
  autopay_charge_now_available?: boolean
  autopay_charge_now_unavailable_reason?: string | null
  balance: string
  balance_cents: number
  accounting_sync_state?: 'Pending' | 'Synced' | 'Error' | null
  accounting_sync_detail?: string | null
  payment_link_available: boolean
  payment_link_url: string | null
  payment_link_unavailable_reason: string | null
  phone_payment_available: boolean
  phone_payment_unavailable_reason: string | null
  line_items: InvoiceLineItem[]
  history: InvoiceHistoryEntry[]
  external_url?: string | null
  last_synced_at?: string | null
  record_payment?: { state: 'enabled' | 'disabled'; message: string | null }
  owner?: Owner | string | null
}

export interface InvoicesResponse extends PagedResponse {
  invoices: Invoice[]
}

export interface InvoiceResponse {
  invoice: Invoice
}

export interface ContactsResponse {
  contacts: Contact[]
}

export interface InvoiceFormLineItem {
  description: string
  amount: string
  taxRateId?: string
}

export interface InvoiceForm {
  contactId: string
  origin: ManualInvoiceOrigin
  issueOn: string
  dueOn: string
  sendEmail: boolean
  lineItems: InvoiceFormLineItem[]
}

export interface PaymentForm {
  amount: string
  method: 'cash' | 'check' | 'wire' | 'other'
  customPaymentTypeId?: number
  paidOn: string
  reference: string
  idempotencyKey: string
  allocations: Array<{ invoiceId: number; amount: string }>
}

export interface PhonePaymentForm {
  paymentMethodId: string
  idempotencyKey: string
}

export interface InvoiceFilters {
  status: InvoiceStatusFilter
  origin: InvoiceOriginFilter
  issuedFrom: string
  issuedThrough: string
  openBalance: boolean
}
